import type { Filter } from "../filters/model";
import type { EscalationReport } from "./escalations";
import type { IsoDate, IsoDateTime, Money } from "../primitives";
import {
  bucketStart,
  fromPaise,
  shiftDate,
  storeDate,
  toPaise,
} from "../orders/series";
import {
  sharedViewDefinition,
  type SharedViewKey,
  type SharedViewList,
} from "../orders/tracking-views";

/**
 * The dashboard (#528): what needs doing today, and how the business is doing.
 *
 * Everything the screen shows is one answer, `DashboardOverview`, which the API
 * counts in Postgres and the fixture adapter counts in memory. What both sides
 * must agree on lives here: which cards exist, which permissions each needs,
 * the filter each opens its list with, the periods and their comparison
 * windows, the colour limits, and the arithmetic of a rate and a median.
 *
 * No order, shipment or customer is in the answer — counts, sums and ages only.
 */

/* ------------------------------------------------------------- action cards */

export const ACTION_CARD_KEYS = [
  "cod_awaiting_confirmation",
  "not_shipped_24h",
  "attempt_failed",
  "cod_payment_pending",
  "no_update",
  "returns_to_receive",
  "low_stock",
  "my_tasks",
] as const;

export type ActionCardKey = (typeof ACTION_CARD_KEYS)[number];

/** Where a card opens, as the router's path and the saved-view scope of the list. */
export interface ActionCardDefinition {
  key: ActionCardKey;
  title: string;
  /** One line under the number: what is being counted. */
  hint: string;
  /** The page the card opens, with the card's filter applied. */
  path: string;
  /** Every one is needed: the card is neither shown nor counted without them. */
  permissions: readonly string[];
  /** The shared saved view that backs the card, when there is one. */
  view: { key: SharedViewKey; list: SharedViewList } | null;
  /**
   * The ticket the card is waiting on, when its number cannot be counted yet.
   * Such a card is drawn as a placeholder: no number, no link.
   */
  waitingOn: string | null;
}

export const ACTION_CARDS: readonly ActionCardDefinition[] = [
  {
    key: "cod_awaiting_confirmation",
    title: "COD awaiting confirmation",
    hint: "COD orders, not cancelled, not shipped, not yet Confirmed.",
    path: "/orders",
    permissions: ["orders.list.view"],
    view: { key: "cod_awaiting_confirmation", list: "orders" },
    waitingOn: null,
  },
  {
    key: "not_shipped_24h",
    title: "Not shipped for over 24 hours",
    hint: "Ready to ship (prepaid, or COD confirmed) with no shipment 24 hours after being placed.",
    path: "/orders",
    permissions: ["orders.list.view"],
    view: { key: "not_shipped_24h", list: "orders" },
    waitingOn: null,
  },
  {
    key: "attempt_failed",
    title: "Delivery attempt failed",
    hint: "Last delivery attempt failed and not yet returning.",
    path: "/orders/shipments",
    permissions: ["orders.shipments.view"],
    view: { key: "attempt_failed", list: "shipments" },
    waitingOn: null,
  },
  {
    key: "cod_payment_pending",
    title: "COD delivered, payment pending",
    hint: "Delivered COD orders still unpaid in Shopify.",
    path: "/orders",
    permissions: ["orders.list.view"],
    view: null,
    waitingOn: null,
  },
  {
    key: "no_update",
    title: "No tracking update",
    hint: "Shipments labelled No update.",
    path: "/orders/shipments",
    permissions: ["orders.shipments.view"],
    view: { key: "no_update", list: "shipments" },
    waitingOn: null,
  },
  {
    key: "returns_to_receive",
    title: "Returns to receive",
    hint: "Returning or arrived parcels not yet confirmed received.",
    path: "/orders/returns",
    permissions: ["orders.returns.receive", "orders.detail.view"],
    view: null,
    waitingOn: null,
  },
  {
    key: "low_stock",
    title: "Low or out of stock",
    hint: "Stock levels at or below their threshold.",
    path: "/inventory/low-stock",
    permissions: ["inventory.lowstock.view"],
    view: null,
    waitingOn: null,
  },
  {
    key: "my_tasks",
    title: "My tasks due today",
    hint: "Your open tasks due today or overdue.",
    path: "/tasks",
    permissions: ["tasks.task.view"],
    view: null,
    waitingOn: null,
  },
];

export function actionCard(key: ActionCardKey): ActionCardDefinition {
  const found = ACTION_CARDS.find((card) => card.key === key);
  if (!found) throw new Error(`no dashboard card ${key}`);
  return found;
}

/** The values an order's financial status holds while Shopify has not been paid. */
export const UNPAID_FINANCIAL_STATUSES = [
  "pending",
  "authorized",
  "partially_paid",
] as const;

function and(...children: Filter["children"]): Filter {
  return { kind: "group", combinator: "and", children };
}

/**
 * The filter a card's list is opened with, when it is not a shared view's.
 *
 * Both engines run these: the API counts a card by compiling the filter against
 * the list's own field map, the fixture adapter by evaluating it against the
 * list's own rows, which is what makes the card and the list unable to disagree.
 * An empty filter is a list whose section already narrows by itself (Returns to
 * receive, Low Stock).
 *
 * `viewerId` is the signed-in person: "my tasks" is a filter on them.
 */
export function standingFilter(
  key: ActionCardKey,
  viewerId: string,
): Filter | null {
  switch (key) {
    case "cod_payment_pending":
      return and(
        { kind: "condition", field: "payment", operator: "is", value: "cod" },
        {
          kind: "condition",
          field: "shipmentStage",
          operator: "is",
          value: "delivered",
        },
        {
          kind: "condition",
          field: "financialStatus",
          operator: "is_any_of",
          value: [...UNPAID_FINANCIAL_STATUSES],
        },
        { kind: "condition", field: "cancelledAt", operator: "is_empty" },
      );
    case "my_tasks":
      return and(
        {
          kind: "condition",
          field: "assigneeUserId",
          operator: "is",
          value: viewerId,
        },
        {
          kind: "condition",
          field: "status",
          operator: "is_none_of",
          value: ["done", "cancelled"],
        },
        {
          kind: "group",
          combinator: "or",
          children: [
            { kind: "condition", field: "overdue", operator: "is_true" },
            { kind: "condition", field: "dueDate", operator: "is_today" },
          ],
        },
      );
    case "returns_to_receive":
    case "low_stock":
      return and();
    default:
      return null;
  }
}

/** The definition of the shared view behind a card, with the filter it ships with. */
export function cardViewDefinition(key: ActionCardKey) {
  const view = actionCard(key).view;
  return view ? sharedViewDefinition(view.key, view.list) : null;
}

/** One action card as the dashboard shows it. */
export interface DashboardActionCard {
  key: ActionCardKey;
  /** `live` has a number; `waiting` is a placeholder for work not built yet. */
  status: "live" | "waiting";
  /** The ticket a waiting card needs. Null when live. */
  waitingOn: string | null;
  /** Null while waiting. */
  count: number | null;
  /** When the oldest counted item began waiting. Null where there is no such time. */
  oldestAt: IsoDateTime | null;
  /** The second line of a card that has one: the out-of-stock count. */
  extra: { label: string; count: number } | null;
  /**
   * The filter the card's list is opened with. For a card backed by a shared
   * view it is that view's stored filter, so an administrator who changes the
   * view changes the card and the list together. Null while waiting.
   */
  filter: Filter | null;
}

/* ------------------------------------------------------------- performance */

export const DASHBOARD_PERIODS = ["today", "7", "30", "90", "mtd"] as const;
export type DashboardPeriod = (typeof DASHBOARD_PERIODS)[number];
export const DEFAULT_DASHBOARD_PERIOD: DashboardPeriod = "7";

/**
 * How many days the dashboard's orders chart covers for a period (#638): the
 * period's own days, so the chart and the cards beside it count the same
 * orders. "Today" is one bar on its own, so it shows the week ending today
 * and the chart names today's figures.
 */
export function trendDays(period: DashboardPeriod, today: IsoDate): number {
  return period === "today" ? 7 : periodDays(period, today);
}

export const DASHBOARD_PERIOD_LABELS: Record<DashboardPeriod, string> = {
  today: "Today",
  "7": "7 days",
  "30": "30 days",
  "90": "90 days",
  mtd: "Month to date",
};

export function isDashboardPeriod(value: unknown): value is DashboardPeriod {
  return (
    typeof value === "string" &&
    (DASHBOARD_PERIODS as readonly string[]).includes(value)
  );
}

/** How many days a period covers; month to date is the month's days so far. */
export function periodDays(period: DashboardPeriod, today: IsoDate): number {
  if (period === "today") return 1;
  if (period === "mtd") return Number(today.slice(8, 10));
  return Number(period);
}

export interface DayRange {
  from: IsoDate;
  to: IsoDate;
}

/**
 * The days a period covers and the days it is compared with (#681).
 *
 * Calendar days of the store, today included: "7 days" is today and the six
 * days before it, compared with the seven before those. "Today" is compared
 * with yesterday. "Month to date" is the 1st to today, compared with the same
 * number of days from the 1st of last month, cut short where last month was
 * shorter, so the 31st is not set against days that were never there.
 */
export function periodWindows(
  period: DashboardPeriod,
  today: IsoDate,
): { current: DayRange; previous: DayRange } {
  const days = periodDays(period, today);
  if (period === "mtd") {
    const firstOfMonth = bucketStart(today, "month");
    const lastOfPrevious = shiftDate(firstOfMonth, -1);
    const firstOfPrevious = bucketStart(lastOfPrevious, "month");
    const sameDays = shiftDate(firstOfPrevious, days - 1);
    return {
      current: { from: firstOfMonth, to: today },
      previous: {
        from: firstOfPrevious,
        to: sameDays > lastOfPrevious ? lastOfPrevious : sameDays,
      },
    };
  }
  return {
    current: { from: shiftDate(today, 1 - days), to: today },
    previous: {
      from: shiftDate(today, 1 - 2 * days),
      to: shiftDate(today, -days),
    },
  };
}

/** Orders placed, and (for someone who may see money) what they came to. */
export interface SalesFigures {
  orders: number;
  /** Null without "view revenue". */
  revenue: Money | null;
  averageOrderValue: Money | null;
}

/** Shipments that reached a final outcome in the period. */
export interface DeliveryFigures {
  /** Delivered plus RTO: what the rates are over. */
  outcomes: number;
  delivered: number;
  rto: number;
  /** Delivered with at most one delivery attempt. */
  deliveredFirstAttempt: number;
}

/** Orders placed in the period, by how they were paid and whether they were cancelled. */
export interface MixFigures {
  placed: number;
  cod: number;
  prepaid: number;
  cancelled: number;
}

/** Medians, in hours, with how many shipments each is the median of. */
export interface SpeedFigures {
  orderToShipHours: number | null;
  orderToShipCount: number;
  shipToDeliverHours: number | null;
  shipToDeliverCount: number;
}

/** A group of cards shown for a period, and the same figures for the period before. */
export interface Compared<T> {
  current: T;
  previous: T;
}

/** One slice of RTO: a courier or a state. */
export interface RtoSlice {
  key: string;
  outcomes: number;
  rto: number;
}

export interface DashboardTrendDay {
  date: IsoDate;
  orders: number;
  /** Null without "view revenue". */
  revenue: Money | null;
}

export const PERFORMANCE_GROUPS = [
  "sales",
  "delivery",
  "mix",
  "speed",
] as const;
export type PerformanceGroup = (typeof PERFORMANCE_GROUPS)[number];

/**
 * What each group needs, which is what the report it opens needs.
 * Sales opens Reports > Sales, Mix opens Reports > Orders (which also counts
 * orders, so it needs the list), Delivery and Speed open Reports > Delivery,
 * which counts shipments.
 */
export const PERFORMANCE_PERMISSIONS: Record<
  PerformanceGroup,
  readonly string[]
> = {
  sales: ["reports.sales.view"],
  delivery: ["reports.sales.view", "orders.shipments.view"],
  mix: ["reports.sales.view", "orders.list.view"],
  speed: ["reports.sales.view", "orders.shipments.view"],
};

/** The permission the money numbers need on top of the report's. */
export const VIEW_REVENUE_PERMISSION = "dashboard.revenue.view";

/** The least a viewer needs to be sent the dashboard route at all. */
export const DASHBOARD_PERMISSION = "dashboard.overview.view";

export interface DashboardOverview {
  generatedAt: IsoDateTime;
  period: DashboardPeriod;
  window: Compared<DayRange>;
  /** Only the cards the viewer may see. Waiting ones are present as placeholders. */
  actions: DashboardActionCard[];
  sales?: Compared<SalesFigures>;
  delivery?: Compared<DeliveryFigures>;
  mix?: Compared<MixFigures>;
  speed?: Compared<SpeedFigures>;
  /** Orders per day for the period (`trendDays`), with revenue for those who may see it. */
  trend?: DashboardTrendDay[];
  rtoByCourier?: RtoSlice[];
  rtoByState?: RtoSlice[];
  limits: DashboardLimits;
}

/* ----------------------------------------------------------------- limits */

/** Every number on the dashboard that is coloured against a limit. */
export const LIMIT_KEYS = [
  ...ACTION_CARD_KEYS,
  "rto_rate",
  "cancellation_rate",
] as const;

export type LimitKey = (typeof LIMIT_KEYS)[number];

/** A value above `amber` is amber; above `red`, red. Rates are percentages. */
export interface DashboardLimit {
  amber: number;
  red: number;
}

export type DashboardLimits = Record<LimitKey, DashboardLimit>;

/**
 * The limits a fresh install uses (#528): an action card is amber when above
 * zero and red above 10, RTO rate amber above 15% and red above 25%,
 * cancellation rate amber above 20% and red above 30%. Edited in
 * Settings > Dashboard.
 */
export const DEFAULT_DASHBOARD_LIMITS: DashboardLimits = {
  cod_awaiting_confirmation: { amber: 0, red: 10 },
  not_shipped_24h: { amber: 0, red: 10 },
  attempt_failed: { amber: 0, red: 10 },
  cod_payment_pending: { amber: 0, red: 10 },
  no_update: { amber: 0, red: 10 },
  returns_to_receive: { amber: 0, red: 10 },
  low_stock: { amber: 0, red: 10 },
  my_tasks: { amber: 0, red: 10 },
  rto_rate: { amber: 15, red: 25 },
  cancellation_rate: { amber: 20, red: 30 },
};

export const LIMIT_LABELS: Record<LimitKey, string> = {
  cod_awaiting_confirmation: "COD awaiting confirmation",
  not_shipped_24h: "Not shipped for over 24 hours",
  attempt_failed: "Delivery attempt failed",
  cod_payment_pending: "COD delivered, payment pending",
  no_update: "No tracking update",
  returns_to_receive: "Returns to receive",
  low_stock: "Low or out of stock",
  my_tasks: "My tasks due today",
  rto_rate: "RTO rate (%)",
  cancellation_rate: "Cancellation rate (%)",
};

export function isRateLimit(key: LimitKey): boolean {
  return key === "rto_rate" || key === "cancellation_rate";
}

export type Tone = "ok" | "amber" | "red";

/** Which colour a figure is: above the red limit, above the amber one, or neither. */
export function toneOf(value: number | null, limit: DashboardLimit): Tone {
  if (value === null) return "ok";
  if (value > limit.red) return "red";
  if (value > limit.amber) return "amber";
  return "ok";
}

/** What is wrong with a set of limits, per field, or an empty object when it is fine. */
export function validateLimits(input: unknown):
  | {
      ok: true;
      limits: DashboardLimits;
    }
  | { ok: false; fields: Record<string, string> } {
  const fields: Record<string, string> = {};
  const record =
    typeof input === "object" && input !== null && !Array.isArray(input)
      ? (input as Record<string, unknown>)
      : null;
  if (!record) return { ok: false, fields: { limits: "Send the limits." } };

  const limits = { ...DEFAULT_DASHBOARD_LIMITS };
  for (const key of Object.keys(record)) {
    if (!(LIMIT_KEYS as readonly string[]).includes(key)) {
      fields[key] = "Not a dashboard card.";
    }
  }
  for (const key of LIMIT_KEYS) {
    const raw = record[key];
    if (raw === undefined) continue;
    if (typeof raw !== "object" || raw === null) {
      fields[key] = "Give an amber and a red limit.";
      continue;
    }
    const { amber, red } = raw as Record<string, unknown>;
    const max = isRateLimit(key) ? 100 : 100000;
    const whole = (value: unknown): value is number =>
      typeof value === "number" && Number.isFinite(value) && value >= 0;
    if (!whole(amber) || !whole(red)) {
      fields[key] = "Limits are numbers, zero or more.";
    } else if (amber > max || red > max) {
      fields[key] = `Limits are at most ${String(max)}.`;
    } else if (
      !isRateLimit(key) &&
      (!Number.isInteger(amber) || !Number.isInteger(red))
    ) {
      fields[key] = "Limits for a count are whole numbers.";
    } else if (red < amber) {
      fields[key] = "The red limit cannot be lower than the amber one.";
    } else {
      limits[key] = { amber, red };
    }
  }
  return Object.keys(fields).length > 0
    ? { ok: false, fields }
    : { ok: true, limits };
}

/* ------------------------------------------------------------- arithmetic */

/** A share as a fraction, or null where there is nothing to divide by. */
export function ratio(part: number, whole: number): number | null {
  return whole > 0 ? part / whole : null;
}

/** A fraction as a percentage rounded to one decimal place. */
export function percent(fraction: number | null): number | null {
  return fraction === null ? null : Math.round(fraction * 1000) / 10;
}

/**
 * How much a figure moved against the period before, relative: `0.12` is up
 * 12%. Null when there was nothing before to compare with, or no figure now.
 */
export function relativeChange(
  current: number | null,
  previous: number | null,
): number | null {
  if (current === null || previous === null || previous === 0) return null;
  return (current - previous) / previous;
}

/** How far a percentage moved, in percentage points. Null when either is unknown. */
export function pointChange(
  current: number | null,
  previous: number | null,
): number | null {
  if (current === null || previous === null) return null;
  return Math.round((current - previous) * 10) / 10;
}

/**
 * The median of a list, the way Postgres' `percentile_cont(0.5)` computes it:
 * the middle value, or the mean of the two in the middle. The API takes its
 * medians from Postgres and the fixture adapter from here, and they are held to
 * one answer.
 */
export function medianOf(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const low = sorted[mid - 1];
  const high = sorted[mid];
  if (high === undefined) return null;
  if (sorted.length % 2 === 1) return high;
  return low === undefined ? high : (low + high) / 2;
}

/** Money added up in exact paise, never as floats. */
export function sumMoney(values: readonly Money[]): Money {
  return fromPaise(values.reduce((total, value) => total + toPaise(value), 0n));
}

/** Revenue divided by orders, to the paisa, from exact paise. Null for no orders. */
export function averageOrderValue(
  revenue: Money,
  orders: number,
): Money | null {
  if (orders <= 0) return null;
  const paise = toPaise(revenue);
  const count = BigInt(orders);
  // Round half up, to the paisa.
  return fromPaise((paise * 2n + count) / (count * 2n));
}

/* ------------------------------------------------- shared row-level meaning */

/** What a shipment contributes to the delivery figures, from its own columns. */
export interface ShipmentPoint {
  stage: string | null;
  attemptCount: number;
  courier: string | null;
  state: string;
  /** `cod`, `prepaid`, or null for an order with no payment gateway. */
  payment: "cod" | "prepaid" | null;
  orderPlacedAt: IsoDateTime;
  shippedAt: IsoDateTime | null;
  deliveredAt: IsoDateTime | null;
  lastScanAt: IsoDateTime | null;
}

/**
 * When a shipment reached its outcome: delivered at the time Shopify says, else
 * the courier's last scan; RTO at the last scan (the scan that said so). Null
 * for a shipment still on its way, which has no outcome yet.
 */
export function shipmentOutcome(
  point: ShipmentPoint,
): { kind: "delivered" | "rto"; at: IsoDateTime } | null {
  if (point.stage === "delivered") {
    const at = point.deliveredAt ?? point.lastScanAt;
    return at ? { kind: "delivered", at } : null;
  }
  if (point.stage === "rto_delivered") {
    return point.lastScanAt ? { kind: "rto", at: point.lastScanAt } : null;
  }
  return null;
}

const HOUR_MS = 3_600_000;

function inRange(instant: IsoDateTime, range: DayRange): boolean {
  const day = storeDate(instant);
  return day >= range.from && day <= range.to;
}

/** The figures of one group of shipments over a range, from their rows. */
export function deliveryFromPoints(
  points: readonly ShipmentPoint[],
  range: DayRange,
): { delivery: DeliveryFigures; speed: SpeedFigures } {
  let delivered = 0;
  let rto = 0;
  let firstAttempt = 0;
  const toShip: number[] = [];
  const toDeliver: number[] = [];

  for (const point of points) {
    const outcome = shipmentOutcome(point);
    const settledInRange = outcome !== null && inRange(outcome.at, range);
    if (settledInRange) {
      if (outcome.kind === "delivered") {
        delivered += 1;
        if (point.attemptCount <= 1) firstAttempt += 1;
        if (point.shippedAt && point.deliveredAt) {
          const span =
            Date.parse(point.deliveredAt) - Date.parse(point.shippedAt);
          if (span >= 0) toDeliver.push(span / HOUR_MS);
        }
      } else {
        rto += 1;
      }
    }
    if (point.shippedAt && inRange(point.shippedAt, range)) {
      const span =
        Date.parse(point.shippedAt) - Date.parse(point.orderPlacedAt);
      if (span >= 0) toShip.push(span / HOUR_MS);
    }
  }

  return {
    delivery: {
      outcomes: delivered + rto,
      delivered,
      rto,
      deliveredFirstAttempt: firstAttempt,
    },
    speed: {
      orderToShipHours: medianOf(toShip),
      orderToShipCount: toShip.length,
      shipToDeliverHours: medianOf(toDeliver),
      shipToDeliverCount: toDeliver.length,
    },
  };
}

/* ----------------------------------------------------------- delivery report */

/** One row of Reports > Delivery: a courier, a state or a payment method. */
export interface DeliveryReportRow extends DeliveryFigures, SpeedFigures {
  key: string;
}

export interface DeliveryReport {
  from: IsoDate;
  to: IsoDate;
  totals: DeliveryFigures & SpeedFigures;
  byCourier: DeliveryReportRow[];
  byState: DeliveryReportRow[];
  byPayment: DeliveryReportRow[];
  /** Escalations by courier (#702): the block under the tables. */
  escalations: EscalationReport;
}

export const NO_COURIER = "Not set";
export const NO_PAYMENT = "Not set";

/** The report's rows from shipments, grouped three ways. */
export function deliveryReportFromPoints(
  points: readonly ShipmentPoint[],
  range: DayRange,
): Omit<DeliveryReport, "escalations"> {
  const groups = (key: (point: ShipmentPoint) => string) => {
    const buckets = new Map<string, ShipmentPoint[]>();
    for (const point of points) {
      const name = key(point);
      const bucket = buckets.get(name);
      if (bucket) bucket.push(point);
      else buckets.set(name, [point]);
    }
    return [...buckets.entries()]
      .map(([name, rows]): DeliveryReportRow => {
        const { delivery, speed } = deliveryFromPoints(rows, range);
        return { key: name, ...delivery, ...speed };
      })
      .filter(
        (row) =>
          row.outcomes > 0 ||
          row.orderToShipCount > 0 ||
          row.shipToDeliverCount > 0,
      )
      .sort(rankRows);
  };
  const total = deliveryFromPoints(points, range);
  return {
    from: range.from,
    to: range.to,
    totals: { ...total.delivery, ...total.speed },
    byCourier: groups((point) => point.courier ?? NO_COURIER),
    byState: groups((point) => point.state),
    byPayment: groups((point) =>
      point.payment === "cod"
        ? "COD"
        : point.payment === "prepaid"
          ? "Prepaid"
          : NO_PAYMENT,
    ),
  };
}

/**
 * The order every breakdown is ranked in: most shipments with an outcome first,
 * then by name, so two sources list ties the same way.
 */
export function rankRows(
  a: { key: string; outcomes: number },
  b: { key: string; outcomes: number },
): number {
  return b.outcomes - a.outcomes || a.key.localeCompare(b.key);
}

/** The slices the dashboard's RTO charts draw: the biggest few by shipments. */
export const RTO_SLICES_SHOWN = 8;

export function rtoSlices(rows: readonly DeliveryReportRow[]): RtoSlice[] {
  return rows
    .filter((row) => row.outcomes > 0)
    .slice(0, RTO_SLICES_SHOWN)
    .map((row) => ({ key: row.key, outcomes: row.outcomes, rto: row.rto }));
}
